"use strict";
/*---------------------------------------------------------------------------------------------
*  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
*--------------------------------------------------------------------------------------------*/
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ExistingPathResolver = void 0;
const CommandExecutor_1 = require("../Utils/CommandExecutor");
const DotnetConditionValidator_1 = require("./DotnetConditionValidator");
const DotnetPathFinder_1 = require("./DotnetPathFinder");
const badExistingPathWarningMessage = `The 'existingDotnetPath' setting was set, but it did not meet the requirements for this extension to run properly.
This setting has been ignored.
If you would like to continue to use the setting anyways, set dotnetAcquisitionExtension.allowInvalidPaths to true in the .NET Install Tool Extension Settings.`;
class ExistingPathResolver {
    constructor(workerContext, utilityContext, executor) {
        var _a;
        this.workerContext = workerContext;
        this.utilityContext = utilityContext;
        this.executor = executor;
        this.lastSeenNonTruePathValue = null;
        (_a = this.executor) !== null && _a !== void 0 ? _a : (this.executor = new CommandExecutor_1.CommandExecutor(this.workerContext, this.utilityContext));
    }
    resolveExistingPath(existingPaths, extensionId, windowDisplayWorker, requirement) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            let existingPath = this.getExistingPath(existingPaths, extensionId, windowDisplayWorker);
            this.lastSeenNonTruePathValue = existingPath;
            if (!this.allowInvalidPath(this.workerContext)) {
                if (!existingPath || existingPath === '""') {
                    return undefined;
                }
                // The user path setting may be a symlink but we dont want to resolve it since a snap symlink isnt a real directory, we can find the true path better this way:
                const oldLookup = process.env.DOTNET_MULTILEVEL_LOOKUP;
                process.env.DOTNET_MULTILEVEL_LOOKUP = '0'; // make it so --list-runtimes only finds the runtimes on that path: https://learn.microsoft.com/en-us/dotnet/core/compatibility/deployment/7.0/multilevel-lookup#reason-for-change
                const dotnetFinder = new DotnetPathFinder_1.DotnetPathFinder(this.workerContext, this.utilityContext, this.executor);
                const resolvedPaths = yield dotnetFinder.returnWithRestoringEnvironment(yield dotnetFinder.getTruePath([existingPath !== null && existingPath !== void 0 ? existingPath : ''], (_b = (_a = this.workerContext.acquisitionContext) === null || _a === void 0 ? void 0 : _a.architecture) !== null && _b !== void 0 ? _b : null), 'DOTNET_MULTILEVEL_LOOKUP', oldLookup);
                existingPath = (_c = resolvedPaths === null || resolvedPaths === void 0 ? void 0 : resolvedPaths[0]) !== null && _c !== void 0 ? _c : null;
            }
            if (existingPath && ((yield this.providedPathMeetsAPIRequirement(this.workerContext, existingPath, this.workerContext.acquisitionContext, requirement)) || this.allowInvalidPath(this.workerContext))) {
                return { dotnetPath: existingPath };
            }
            return undefined;
        });
    }
    /**
     * @remarks Returns the last path this class processed before getting the true path.
     * This is a bit of a hack to allow tests to evaluate the class more thoroughly.
     */
    getlastSeenNonTruePathValue() {
        return this.lastSeenNonTruePathValue;
    }
    getExistingPath(existingPaths, extensionId, windowDisplayWorker) {
        var _a, _b, _c, _d;
        if (existingPaths && (((_b = (_a = existingPaths === null || existingPaths === void 0 ? void 0 : existingPaths.individualizedExtensionPaths) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0 || (existingPaths === null || existingPaths === void 0 ? void 0 : existingPaths.sharedExistingPath))) {
            if (!extensionId) {
                // Use the global path if it is specified
                if (existingPaths.sharedExistingPath) {
                    return existingPaths.sharedExistingPath;
                }
                else {
                    windowDisplayWorker.showWarningMessage('Ignoring existing .NET paths defined in settings.json because requesting extension does not define its extension ID. Please file a bug against the requesting extension.', () => { });
                    return null;
                }
            }
            else {
                const matchingExtensions = (_c = existingPaths.individualizedExtensionPaths) === null || _c === void 0 ? void 0 : _c.filter((pair) => pair.extensionId === extensionId);
                if (matchingExtensions && matchingExtensions.length > 0) {
                    const existingLocalPath = (_d = existingPaths.individualizedExtensionPaths) === null || _d === void 0 ? void 0 : _d.filter((pair) => pair.extensionId === extensionId);
                    if (existingLocalPath && existingLocalPath.length > 0) {
                        return existingLocalPath[0].path;
                    }
                }
                else if (existingPaths.sharedExistingPath) {
                    return existingPaths.sharedExistingPath;
                }
                else {
                    windowDisplayWorker.showWarningMessage(`Ignoring existing .NET paths defined in settings.json because the setting is only set for other extensions, and not for ${extensionId}`, () => { });
                    return null;
                }
            }
        }
        return null;
    }
    allowInvalidPath(workerContext) {
        var _a;
        return (_a = workerContext.allowInvalidPathSetting) !== null && _a !== void 0 ? _a : false;
    }
    providedPathMeetsAPIRequirement(workerContext, existingPath, apiRequest, requirement) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const validator = new DotnetConditionValidator_1.DotnetConditionValidator(this.workerContext, this.utilityContext, this.executor);
            const validated = yield validator.dotnetMeetsRequirement(existingPath, { acquireContext: apiRequest, versionSpecRequirement: requirement !== null && requirement !== void 0 ? requirement : 'equal' });
            if (!validated && !this.allowInvalidPath(workerContext)) {
                this.utilityContext.ui.showWarningMessage(`${badExistingPathWarningMessage}\nExtension: ${(_a = workerContext.acquisitionContext.requestingExtensionId) !== null && _a !== void 0 ? _a : 'Unspecified'}`, () => { });
            }
            return validated;
        });
    }
}
exports.ExistingPathResolver = ExistingPathResolver;
//# sourceMappingURL=ExistingPathResolver.js.map